"use client";

import { c as _c } from "react/compiler-runtime";
import DropListItemContentMedia from "@/components/drops/view/item/content/media/DropListItemContentMedia";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { getDropPreviewImageUrl, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import clsx from "clsx";
import type { ReactNode, TouchEventHandler } from "react";
import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import MemesQuickVoteDescription from "./MemesQuickVoteDescription";
import MemesQuickVoteDropHeader from "./MemesQuickVoteDropHeader";
import useMemesQuickVotePreviewSwipe from "./useMemesQuickVotePreviewSwipe";
const MOBILE_SWIPE_CENTER_SLIDE_INDEX = 1;
interface MemesQuickVotePreviewProps {
  readonly drop: ExtendedDrop;
  readonly isBusy: boolean;
  readonly isMobile: boolean;
  readonly remainingCount: number;
  readonly swipeVoteAmount: number | null;
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
  readonly onAdvanceStart: () => void;
  readonly onSkip: () => void;
  readonly onVoteWithSwipe: () => void;
}
interface MemesQuickVoteTouchSurfaceProps {
  readonly onTouchCancel: TouchEventHandler<HTMLDivElement>;
  readonly onTouchEnd: TouchEventHandler<HTMLDivElement>;
  readonly onTouchMove: TouchEventHandler<HTMLDivElement>;
  readonly onTouchStart: TouchEventHandler<HTMLDivElement>;
}
function getQuickVoteArtworkMediaContent({
  artworkMedia,
  hasTouchScreen,
  htmlPreviewImageUrl
}: {
  readonly artworkMedia: ExtendedDrop["parts"][number]["media"][number] | undefined;
  readonly hasTouchScreen: boolean;
  readonly htmlPreviewImageUrl?: string | undefined;
}): ReactNode {
  if (!artworkMedia) {
    return null;
  }
  return <DropListItemContentMedia media_mime_type={artworkMedia.mime_type} media_url={artworkMedia.url} isCompetitionDrop={true} disableAutoPlay={hasTouchScreen} disableModal={hasTouchScreen} htmlPreviewImageUrl={htmlPreviewImageUrl} />;
}
function MemesQuickVoteMobileSwipeSurface(t0) {
  const $ = _c(7);
  const {
    className,
    touchSurfaceProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      touchAction: "pan-y"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className || $[2] !== touchSurfaceProps.onTouchCancel || $[3] !== touchSurfaceProps.onTouchEnd || $[4] !== touchSurfaceProps.onTouchMove || $[5] !== touchSurfaceProps.onTouchStart) {
    t2 = <div aria-hidden="true" className={className} style={t1} onTouchStart={touchSurfaceProps.onTouchStart} onTouchMove={touchSurfaceProps.onTouchMove} onTouchEnd={touchSurfaceProps.onTouchEnd} onTouchCancel={touchSurfaceProps.onTouchCancel} />;
    $[1] = className;
    $[2] = touchSurfaceProps.onTouchCancel;
    $[3] = touchSurfaceProps.onTouchEnd;
    $[4] = touchSurfaceProps.onTouchMove;
    $[5] = touchSurfaceProps.onTouchStart;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function MemesQuickVoteMobileDetails(t0) {
  const $ = _c(20);
  const {
    description,
    drop,
    title,
    touchSurfaceProps
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      touchAction: "pan-y"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== drop) {
    t2 = <MemesQuickVoteDropHeader drop={drop} />;
    $[1] = drop;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== title) {
    t3 = <h2 className="tw-mb-0 tw-text-[1.15rem] tw-font-semibold tw-leading-tight tw-tracking-tight tw-text-white">{title}</h2>;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== description || $[6] !== drop) {
    t4 = description && <MemesQuickVoteDescription key={drop.id} description={description} />;
    $[5] = description;
    $[6] = drop;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="tw-space-y-3">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-6 tw-pb-5 tw-pt-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden"><div className="tw-flex tw-flex-col tw-gap-4">{t2}{t5}</div></div>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6 || $[15] !== touchSurfaceProps.onTouchCancel || $[16] !== touchSurfaceProps.onTouchEnd || $[17] !== touchSurfaceProps.onTouchMove || $[18] !== touchSurfaceProps.onTouchStart) {
    t7 = <div className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-[#0d0d0e] md:tw-hidden" style={t1} onTouchStart={touchSurfaceProps.onTouchStart} onTouchMove={touchSurfaceProps.onTouchMove} onTouchEnd={touchSurfaceProps.onTouchEnd} onTouchCancel={touchSurfaceProps.onTouchCancel}>{t6}</div>;
    $[14] = t6;
    $[15] = touchSurfaceProps.onTouchCancel;
    $[16] = touchSurfaceProps.onTouchEnd;
    $[17] = touchSurfaceProps.onTouchMove;
    $[18] = touchSurfaceProps.onTouchStart;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
function MemesQuickVoteMediaStage(t0) {
  const $ = _c(23);
  const {
    isMobile,
    mediaContent,
    swipeHint,
    swipeOffset,
    touchSurfaceProps
  } = t0;
  if (mediaContent === null) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-flex tw-h-[45vh] tw-w-full tw-shrink-0 tw-items-center tw-justify-center tw-border-b tw-border-solid tw-border-white/5 tw-bg-black/30 tw-text-sm tw-text-iron-500 md:tw-h-full md:tw-border-0">Preview unavailable</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const t1 = isMobile ? "tw-h-[45vh] tw-border-b tw-border-solid tw-border-white/5" : "md:tw-flex md:tw-h-full md:tw-w-full md:tw-items-center md:tw-justify-center md:tw-border-0";
  let t2;
  if ($[1] !== t1) {
    t2 = clsx("tw-relative tw-shrink-0 tw-overflow-hidden tw-bg-black/40", t1);
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== mediaContent) {
    t3 = <div className="tw-flex tw-h-full tw-items-center tw-justify-center md:tw-h-full md:tw-w-full">{mediaContent}</div>;
    $[3] = mediaContent;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isMobile || $[6] !== touchSurfaceProps) {
    t4 = isMobile && (["left", "right"] as const).map(side => <MemesQuickVoteMobileSwipeSurface key={side} className={clsx("tw-absolute tw-inset-y-0 tw-z-20 tw-w-12 md:tw-hidden", side === "left" ? "tw-left-0" : "tw-right-0")} touchSurfaceProps={touchSurfaceProps} />);
    $[5] = isMobile;
    $[6] = touchSurfaceProps;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = swipeOffset < 0 ? "tw-opacity-100" : "tw-opacity-0";
  let t6;
  if ($[8] !== t5) {
    t6 = clsx("tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-4 tw-z-10 tw-flex tw-items-center tw-text-sm tw-font-semibold tw-text-rose-300 tw-transition-opacity md:tw-hidden", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <div className={t6}>Skip</div>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = swipeOffset > 0 ? "tw-opacity-100" : "tw-opacity-0";
  let t9;
  if ($[12] !== t8) {
    t9 = clsx("tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-4 tw-z-10 tw-flex tw-items-center tw-text-right tw-text-sm tw-font-semibold tw-text-primary-300 tw-transition-opacity md:tw-hidden", t8);
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = swipeHint ? `Vote ${swipeHint}` : "Vote";
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t7) {
    t12 = <div className={t2}>{t3}{t4}{t7}{t11}</div>;
    $[17] = t11;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t7;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function MemesQuickVotePreviewContent(t0) {
  const $ = _c(71);
  const {
    drop,
    isBusy,
    isMobile,
    remainingCount,
    swipeVoteAmount,
    uncastPower,
    votingLabel,
    onAdvanceStart,
    onSkip,
    onVoteWithSwipe
  } = t0;
  const title = drop.metadata.find(_temp)?.data_value ?? "Untitled submission";
  const description = drop.metadata.find(_temp2)?.data_value ?? "";
  const artworkMedia = drop.parts.at(0)?.media.at(0);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const isInteractiveHtmlMedia = artworkMedia?.mime_type === "text/html";
  const htmlPreviewImageUrl = isInteractiveHtmlMedia && hasTouchScreen ? getDropPreviewImageUrl(drop.metadata) ?? undefined : undefined;
  let t1;
  bb0: {
    if (swipeVoteAmount === null) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== swipeVoteAmount) {
      t2 = formatNumberWithCommas(swipeVoteAmount);
      $[0] = swipeVoteAmount;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    t1 = `${t2} ${votingLabel ?? "votes"}`;
  }
  const swipeHint = t1;
  const swipeInstructionText = swipeHint ? `Swipe left to skip, right to vote ${swipeHint}` : null;
  let t2;
  if ($[2] !== isBusy || $[3] !== isMobile || $[4] !== onAdvanceStart || $[5] !== onSkip || $[6] !== onVoteWithSwipe || $[7] !== swipeVoteAmount) {
    t2 = {
      isBusy,
      isMobile,
      onAdvanceStart,
      onSkip,
      onVoteWithSwipe,
      swipeVoteAmount
    };
    $[2] = isBusy;
    $[3] = isMobile;
    $[4] = onAdvanceStart;
    $[5] = onSkip;
    $[6] = onVoteWithSwipe;
    $[7] = swipeVoteAmount;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const {
    previewCardRef,
    canUseSwiperTouchSurface,
    cardTransform,
    cardTransitionDuration,
    handleCardTransitionEnd,
    handleTouchSurfaceCancel,
    handleTouchSurfaceEnd,
    handleTouchSurfaceMove,
    handleTouchSurfaceStart,
    handleSwiperMove,
    handleSwiperTouchEnd,
    swipeOffset
  } = useMemesQuickVotePreviewSwipe(t2);
  const mediaContent = getQuickVoteArtworkMediaContent({
    artworkMedia,
    hasTouchScreen,
    htmlPreviewImageUrl
  });
  let t3;
  if ($[9] !== handleTouchSurfaceCancel) {
    t3 = event => {
      handleTouchSurfaceCancel(event);
    };
    $[9] = handleTouchSurfaceCancel;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== handleTouchSurfaceEnd) {
    t4 = event_0 => {
      handleTouchSurfaceEnd(event_0);
    };
    $[11] = handleTouchSurfaceEnd;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== handleTouchSurfaceMove) {
    t5 = event_1 => {
      handleTouchSurfaceMove(event_1);
    };
    $[13] = handleTouchSurfaceMove;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== handleTouchSurfaceStart) {
    t6 = event_2 => {
      handleTouchSurfaceStart(event_2);
    };
    $[15] = handleTouchSurfaceStart;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = {
      onTouchCancel: t3,
      onTouchEnd: t4,
      onTouchMove: t5,
      onTouchStart: t6
    };
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const mobileTouchSurfaceProps = t7;
  const t8 = cardTransform ?? undefined;
  const t9 = isBusy && "tw-pointer-events-none tw-opacity-70";
  let t10;
  if ($[22] !== t9) {
    t10 = clsx("tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-transition-all tw-duration-200 tw-ease-out", t9);
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = isMobile ? "pan-y" : undefined;
  let t12;
  if ($[24] !== cardTransform || $[25] !== cardTransitionDuration || $[26] !== t11) {
    t12 = {
      transform: cardTransform,
      touchAction: t11,
      transitionDuration: cardTransitionDuration
    };
    $[24] = cardTransform;
    $[25] = cardTransitionDuration;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== isMobile || $[29] !== mediaContent || $[30] !== mobileTouchSurfaceProps || $[31] !== swipeHint || $[32] !== swipeOffset) {
    t13 = <MemesQuickVoteMediaStage isMobile={isMobile} mediaContent={mediaContent} swipeHint={swipeHint} swipeOffset={swipeOffset} touchSurfaceProps={mobileTouchSurfaceProps} />;
    $[28] = isMobile;
    $[29] = mediaContent;
    $[30] = mobileTouchSurfaceProps;
    $[31] = swipeHint;
    $[32] = swipeOffset;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== description || $[35] !== drop || $[36] !== mobileTouchSurfaceProps || $[37] !== title) {
    t14 = <MemesQuickVoteMobileDetails description={description} drop={drop} title={title} touchSurfaceProps={mobileTouchSurfaceProps} />;
    $[34] = description;
    $[35] = drop;
    $[36] = mobileTouchSurfaceProps;
    $[37] = title;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14) {
    t15 = <div data-testid="quick-vote-preview-mobile-context" className="tw-flex tw-h-full tw-flex-col md:tw-flex md:tw-min-h-0 md:tw-flex-1 md:tw-p-0">{t13}{t14}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== handleCardTransitionEnd || $[43] !== previewCardRef || $[44] !== t10 || $[45] !== t12 || $[46] !== t15 || $[47] !== t8) {
    t16 = <article ref={previewCardRef} data-testid="quick-vote-preview-card" data-quick-vote-transform={t8} className={t10} style={t12} onTransitionEnd={handleCardTransitionEnd}>{t15}</article>;
    $[42] = handleCardTransitionEnd;
    $[43] = previewCardRef;
    $[44] = t10;
    $[45] = t12;
    $[46] = t15;
    $[47] = t8;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  const previewCard = t16;
  let t17;
  if ($[49] !== swipeInstructionText) {
    t17 = swipeInstructionText && <span className="tw-sr-only">{swipeInstructionText}</span>;
    $[49] = swipeInstructionText;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== uncastPower || $[52] !== votingLabel) {
    t18 = typeof uncastPower === "number" && <span className="tw-sr-only tw-text-primary-300">{formatNumberWithCommas(uncastPower)} {votingLabel ?? "votes"} left</span>;
    $[51] = uncastPower;
    $[52] = votingLabel;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== remainingCount) {
    t19 = formatNumberWithCommas(remainingCount);
    $[54] = remainingCount;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t19) {
    t20 = <span aria-hidden="true" className="tw-rounded-full tw-border tw-border-solid tw-border-white/5 tw-bg-white/[0.03] tw-px-4 tw-py-1.5 tw-text-[13px] tw-font-bold tw-text-iron-300 tw-shadow-sm tw-backdrop-blur-md">{t19} unexplored</span>;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== t17 || $[59] !== t18 || $[60] !== t20) {
    t21 = <div data-testid="quick-vote-preview-status" className="tw-sr-only">{t17}{t18}{t20}</div>;
    $[58] = t17;
    $[59] = t18;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  let t22;
  if ($[62] !== canUseSwiperTouchSurface || $[63] !== handleSwiperMove || $[64] !== handleSwiperTouchEnd || $[65] !== isBusy || $[66] !== previewCard) {
    t22 = <div className="tw-relative tw-min-h-0 tw-flex-1">{canUseSwiperTouchSurface ? <Swiper initialSlide={MOBILE_SWIPE_CENTER_SLIDE_INDEX} slidesPerView={1} watchOverflow={false} followFinger={false} longSwipes={false} shortSwipes={false} threshold={0} allowTouchMove={!isBusy} resistanceRatio={0} touchStartPreventDefault={false} className="tw-h-full tw-overflow-visible" onTouchMove={handleSwiperMove} onTouchEnd={handleSwiperTouchEnd}><SwiperSlide aria-hidden="true" className="tw-h-full"><div className="tw-h-px" /></SwiperSlide><SwiperSlide className="tw-h-full">{previewCard}</SwiperSlide><SwiperSlide aria-hidden="true" className="tw-h-full"><div className="tw-h-px" /></SwiperSlide></Swiper> : previewCard}</div>;
    $[62] = canUseSwiperTouchSurface;
    $[63] = handleSwiperMove;
    $[64] = handleSwiperTouchEnd;
    $[65] = isBusy;
    $[66] = previewCard;
    $[67] = t22;
  } else {
    t22 = $[67];
  }
  let t23;
  if ($[68] !== t21 || $[69] !== t22) {
    t23 = <div className="tw-flex tw-h-full tw-flex-col">{t21}{t22}</div>;
    $[68] = t21;
    $[69] = t22;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  return t23;
}
function _temp2(entry_0) {
  return entry_0.data_key === "description";
}
function _temp(entry) {
  return entry.data_key === "title";
}
export default function MemesQuickVotePreview(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <MemesQuickVotePreviewContent key={props.drop.id} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}